import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  Switch,
  FormControlLabel,
  TextField,
  Grid,
  InputAdornment,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import DateRangeIcon from '@mui/icons-material/DateRange';

const getCancelledTransactions = async (token, agencyId, retrieveAll = false) => {
  const baseURL = process.env.REACT_APP_BASE_URL;
  try {
    // Construct the URL with or without the query parameter
    const url = retrieveAll
      ? `${baseURL}PostingTransferBond/${agencyId}/Cancelled?retrieveAll=true`
      : `${baseURL}PostingTransferBond/${agencyId}/Cancelled`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Error fetching cancelled transactions: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching cancelled transactions:', error);
    toast.error('Error fetching cancelled transactions.');
    throw error;
  }
};

const CancelledTransactionsGrid = ({ selectedAgency, canViewRequestingAgency }) => {
  const navigate = useNavigate(); // For navigation to view details
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [searchRef, setSearchRef] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Fetch the cancelled transactions data
  const fetchCancelledTransactions = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('jwtToken');
    if (!token || !selectedAgency) {
      toast.error('No authentication token or selected agency found.');
      setLoading(false);
      return;
    }

    try {
      const data = await getCancelledTransactions(token, selectedAgency.id, showAll);

      // Process the data
      const processedData = data.map((transaction) => {
        let dateCancelledFormatted = 'N/A';
        if (transaction.cancelledDate) {
          const dateObj = new Date(transaction.cancelledDate);
          const dateStr = dateObj.toLocaleDateString('en-US');
          const timeStr = dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          dateCancelledFormatted = { date: dateStr, time: timeStr };
        }

        return {
          id: transaction.transferBondTransactionID,
          refNumber: transaction.transferBondTransactionID.toString(),
          defendantName: `${transaction.defendantFirstName?.trim() || ''} ${transaction.defendantLastName?.trim() || ''}`.trim() || 'N/A',
          requestingAgencyName: transaction.requestingAgencyName || '-',
          reason: transaction.cancelledReason || 'N/A',
          dateCancelled: transaction.cancelledDate ? new Date(transaction.cancelledDate) : null,
          dateCancelledFormatted: dateCancelledFormatted,
          details: transaction.cancelledReason || 'No details provided',
        };
      });

      setRecords(processedData);
    } catch (error) {
      // Error handled in the API call
    } finally {
      setLoading(false);
    }
  }, [showAll, selectedAgency]);

  useEffect(() => {
    fetchCancelledTransactions();
  }, [fetchCancelledTransactions]);

  const handleViewDetails = (id) => {
    navigate(`/view-quote-details?ID=${id}`);
  };

  const columns = [
    {
      field: 'refNumber',
      headerName: 'Ref #',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          height: '100%',
          width: '100%'
        }}>
          <Typography variant="body2">{params.value}</Typography>
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleViewDetails(params.row.id)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    {
      field: 'defendantName',
      headerName: 'Defendant Name',
      width: 250,
      align: 'center',
      headerAlign: 'center',
    },
    ...(canViewRequestingAgency
          ? [
              {
                field: 'requestingAgencyName',
                headerName: 'Requesting Agency',
                width: 200,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textAlign: 'center',
                      height: '100%',
                      width: '100%',
                      overflow: 'hidden',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      fontSize: '0.85rem',
                      lineHeight: 1.4,
                    }}
                  >
                    {params.row.requestingAgencyName || '-'}
                  </Box>
                ),
              },
            ]
          : []),
    { field: 'reason', headerName: 'Reason', width: 200 },
    {
      field: 'dateCancelledFormatted',
      headerName: 'Date Cancelled',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        if (params.value === 'N/A' || typeof params.value === 'string') {
          return <Typography variant="body2">N/A</Typography>;
        }
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2">{params.value.date}</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
              {params.value.time}
            </Typography>
          </Box>
        );
      },
    },
    { field: 'details', headerName: 'Details', width: 250 },
  ];

  // Filter the data based on search input and date range
  const getFilteredRows = () => {
    let filteredRecords = records;

    // Filter by reference number
    if (searchRef.trim() !== '') {
      filteredRecords = filteredRecords.filter((record) =>
        record.refNumber.includes(searchRef.trim())
      );
    }

    // Filter by date range (only when showing all)
    if (showAll && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      // Include end date's entire day
      end.setHours(23, 59, 59, 999);

      filteredRecords = filteredRecords.filter(
        (record) =>
          record.dateCancelled &&
          record.dateCancelled >= start &&
          record.dateCancelled <= end
      );
    }

    return filteredRecords;
  };

  return (
    <Paper sx={{ padding: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Cancelled Transactions
      </Typography>

      {/* Toggle to Show All Cancelled Transactions */}
      <FormControlLabel
        control={
          <Switch
            checked={showAll}
            onChange={(e) => {
              setShowAll(e.target.checked);
              // Reset search inputs when toggling
              setSearchRef('');
              setStartDate('');
              setEndDate('');
            }}
            color="primary"
          />
        }
        label={
          showAll ? 'Viewing All Cancelled Transactions' : 'Viewing Recent Cancelled Transactions'
        }
      />

      {/* Search and Date Filters */}
      <Box sx={{ mt: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Reference Number Search */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Search by Reference #"
              value={searchRef}
              onChange={(e) => setSearchRef(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Date Range Filters (Only when showing all) */}
          {showAll && (
            <>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DateRangeIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DateRangeIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
          }}
        >
          <CircularProgress />
        </Box>
      ) : records.length > 0 ? (
        getFilteredRows().length > 0 ? (
          <Paper sx={{ width: '100%' }}>
            <DataGrid
              rows={getFilteredRows()}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 },
                },
              }}
              pageSizeOptions={[10]}
              pagination
              autoHeight
              sx={{ fontSize: '0.85rem' }}
            />
          </Paper>
        ) : (
          // No records after filtering
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
            }}
          >
            <Typography variant="body1" color="textSecondary">
              No transactions found for the given criteria.
            </Typography>
          </Box>
        )
      ) : (
        // No data from API
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
          }}
        >
          <Typography variant="body1" color="textSecondary">
            No cancelled transactions found.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default CancelledTransactionsGrid;
